import React, {
	useCallback,
	useContext,
	useMemo,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import type { Shot, ShotFeed } from '../../../stores/types/shots';
import { GreenroomState as GS } from '../../../stores/types/types';
import Socket from '../../../connection/socket';
import useShots from '../../../contexts/shots/provider';
import { useOpenShotModal } from '../../../hooks/use-shot-utilities';
import useUserActions from '../../../hooks/use-user-actions';
import { editShot } from '../../../stores/actions/shots';
import * as SettingsActions from '../../../stores/actions/settings';
import { ROOM_STATES, SETTINGS_TABS } from '../../../stores/reducers/settings';
import type { GraphicInterface } from '../../../stores/types/graphics';
import { SET_CURRENT_GRAPHICS_ID, setCurrentGraphicsId } from '../../../stores/actions/graphics';
import { transitionEffects, transitionOutEffects } from '../settings/animations/types';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import Switch from '../../general-components/switch';
import ImageCardToggle from '../../custom-graphics/image-card-toggle';
import {
	removeAnimatedOverlay,
	updateOverlayInAllShotOverlays,
} from '../../../connection/graphics';
import { SocketContext } from '../../../contexts/socket';
import { setActiveTab } from '../../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES, TABS } from '../../../stores/types/tabs';
import { ContentTemplate } from '../../types/stylepacks';
import { openStrictStylePackMenu } from '../../../utils/content-templates';

interface ShotDrawerProps {
	shotTabShot: Shot;
	socket: Socket | null;
}

export default function OverlayCards(props: ShotDrawerProps) {
	const {
		shotTabShot,
	} = props;

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const allGraphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const enabledOverlays = useSelector((s: GS) => s.GraphicsReducer.enabledOverlays);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const { metaMenuState, metaMenuActiveRoute } = useSelector((s: GS) => s.TabsReducer);
	const displayedStylePackInfo = useSelector((s: GS) => s.UserInterfaceReducer.displayedStylePackInfo);
	const {
		titlesAnimation,
		titlesDuration,
	} = useSelector((s: GS) => s.ShotsReducer.canvasStyle);
	const useNewShotThumbs = useSelector((s: GS) => s.SettingsReducer.featureFlags.use_new_shot_thumbs);

	const { socket } = useContext(SocketContext);

	const dispatch = useDispatch();

	const {
		currentShot,
	} = useShots();

	const currentShotId = currentShot.shot_id;

	const handleOpenShotModal = useOpenShotModal();

	const {
		toggleTitles,
		titlesShowing,
	} = useUserActions();

	const {
		shot_id: shotId,
		shot_data: shotData,
	} = shotTabShot;

	const {
		videoTitlesOn,
		shotGraphics,
	} = shotData;

	const toggleTitlesForThisShot = useCallback(() => {
		if (!token) return;

		const newValue = !videoTitlesOn;
		const newShot = {
			...shotTabShot,
			shot_data: {
				...shotData,
				videoTitlesOn: newValue,
			},
		};

		dispatch(editShot(token, newShot, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
		socket?.sendMessage('toggle-all-titles', [[0], newShot.shot_id]);
	}, [token, shotTabShot, videoTitlesOn, meeting, useNewShotThumbs]);

	const openDesignTitlesTab = () => {
		dispatch(setActiveTab(TABS.SETTINGS));
		dispatch(SettingsActions.setSettingsTab(SETTINGS_TABS.TITLES));
	};

	const titleAnimationText = useMemo(() => {
		switch (titlesAnimation) {
			case 'dissolve':
				return 'Dissolve';
			case 'move-in':
				return 'Move In';
			default:
				return 'None';
		}
	}, [titlesAnimation]);

	const titleDurationText = useMemo(() => {
		switch (titlesDuration) {
			case 10:
				return '10 Seconds';
			case 5:
				return '5 Seconds';
			default:
				return 'All Time';
		}
	}, [titlesDuration]);

	// Get array of overlay objects in current shot
	const currentShotGraphics = useMemo((): GraphicInterface[] => allGraphics.filter(
		(graphic) => {
			if (graphic?.id) {
				return graphic.is_global || shotGraphics?.includes(graphic.id);
			}
			return false;
		},
	), [allGraphics, shotGraphics]);

	// Get an id array of overlays currently enabled for the shot
	const enabledGraphicsCurrentShot = useMemo((): number[] => currentShotGraphics.reduce<number[]>(
		(idArray: number[], graphic: GraphicInterface) => {
			const { id } = graphic;

			if (id && enabledOverlays?.includes(id)) {
				return [...idArray, id];
			}

			return idArray;
		},
		[],
	), [currentShotGraphics, enabledOverlays]);

	const graphicIsEnabled = useCallback((id?: number): boolean => {
		const isGraphicInShot = !!currentShot.shot_data.shotGraphics?.find(
			(overlayId) => overlayId === id,
		);
		const isGlobalGraphic = !!currentShotGraphics.find(
			(graphic) => graphic?.id === id && graphic.is_global,
		);

		return isGraphicInShot || isGlobalGraphic;
	}, [currentShot, currentShotGraphics]);

	const toggleGraphicState = useCallback(
		(id?: number) => {
			if (
				token
				&& id
				&& meeting
				&& graphicIsEnabled(id)
			) {
				updateOverlayInAllShotOverlays(
					token,
					meeting.meeting,
					id,
					!enabledGraphicsCurrentShot.includes(id),
				);
				removeAnimatedOverlay(
					token,
					meeting.meeting,
					id,
				);
			}
		},
		[enabledGraphicsCurrentShot],
	);

	const getAnimationSubtitle = useCallback(
		(id?: number): JSX.Element[] => {
			if (id) {
				const selectedGraphic = currentShotGraphics.find(
					(graphic) => graphic?.id === id,
				);

				if (selectedGraphic?.animation) {
					const {
						build_in: buildIn = 0,
						build_out: buildOut = 0,
						delay_in: delayIn = 0,
						show_for: showFor = 0,
					} = selectedGraphic.animation;

					// eslint-disable-next-line valid-typeof
					const alwaysNumber = (input?: number) => (typeof input === undefined ? -1 : input);

					const buildInLabel = alwaysNumber(buildIn) !== -1
						? transitionEffects[buildIn as number]?.label
						: '';
					const buildOutLabel = alwaysNumber(buildOut) !== -1
						? transitionOutEffects[buildOut as number]?.label
						: '';

					const buildInSeconds = buildInLabel === 'After delay'
						? `${delayIn}s`
						: '';

					const buildOutSeconds = buildOutLabel === 'After delay'
						? `${showFor}s`
						: '';

					return [
						(<><span className='slightly-bold'>In</span>: {buildInLabel} {buildInSeconds}</>),
						(<><span className='slightly-bold'>Out</span>: {buildOutLabel} {buildOutSeconds}</>),
					];
				}
			}

			return [
				(<><span className='bold'>Build In</span>: None</>),
				(<><span className='bold'>Build Out</span>: None</>),
			];
		},
		[currentShotGraphics],
	);

	const editOverlay = (id: number | undefined = 0) => {
		if (roomState === ROOM_STATES.EDITING) {
			/*
				The underlying issue with just setting the current graphics ID
				is that because of how the overlays feature is built, the value
				is sometimes not unset when `editOverlay` is called.

				We could try to work out the logic for when to set
				`currentGraphicId` to 0, but the easiest most `useEffect`-free
				solution is to just open the overlay animation drawer if the
				value is already set.
			*/
			if (!currentGraphicId) {
				dispatch({
					type: SET_CURRENT_GRAPHICS_ID,
					payload: id,
				});
			} else {

				let contentTemplate:ContentTemplate | undefined;
				let graphic = allGraphics.find(x => x.id == id);

				if (graphic?.template_type) {
					contentTemplate = contentTemplates.find(x => x.type == graphic?.template_type);
				}

				if (contentTemplate?.data?.strict) {
					const isOpenToStylePack = metaMenuState === META_MENU_STATES.OPEN &&
									metaMenuActiveRoute === META_MENU_ROUTES.STYLE_PACK_INFO &&
									displayedStylePackInfo?.id === contentTemplate.stylePack;
					if (!isOpenToStylePack) {
						openStrictStylePackMenu(dispatch, contentTemplates, contentTemplate);
					}
				} else {
					dispatch(setActiveTab(TABS.OVERLAYS));
				}
			}
		} else if (roomState === ROOM_STATES.SHOWTIME) {
			handleOpenShotModal(shotTabShot, socket);
			dispatch(setCurrentGraphicsId(id));
		}
	};

	const hasPresenter = useMemo(
		() => shotTabShot.shot_data.shotFeeds.some(
			(s: ShotFeed) => ((s.presenter || s.onsiteLocationId) && !s.audioOnly),
		),
		[shotTabShot.shot_data.shotFeeds],
	);

	const allCollapsed = currentShotGraphics.length === 0 && !hasPresenter;

	return (<>
		{allCollapsed ? <></> : <label className="shots-label">Overlays</label>}

		<div className={classNames('overlays', { allCollapsed })}>
			<div className={classNames(
				'switch-row padding-medium corner-round-large margin-top-medium align-items-center justify-content-space-between',
				{
					'toggled-on': videoTitlesOn,
					collapsed: !hasPresenter,
				},
			)}>
					<div className="flex-column">
						<span className="font-weight-heavy line-height-20">Titles</span>
						<span className="text-gray line-height-20">{titleAnimationText}, {titleDurationText}</span>
					</div>
					{
						shotId === currentShotId
						&& videoTitlesOn
						&& titlesDuration && (
							<button className="title-play round background-green" onMouseDown={toggleTitles}>
								<span className="font-size-14 line-height-16 text-dark">
									{titlesShowing ? 'Hide' : 'Show'}
								</span>
							</button>
						)
					}
					<div className="flex-align-center">
						<button className="transparent padding-small margin-right-small" onClick={openDesignTitlesTab}>
							<Icon name={ICONS.EDIT} size={16} color={COLORS.LIGHT_GRAY} />
						</button>
						<Switch on={videoTitlesOn} onChange={toggleTitlesForThisShot} className='green' />
					</div>
			</div>
			{
				currentShotGraphics.map(({ id, title, thumbnail_url: thumbnailUrl }) => (
					<ImageCardToggle
						key={id}
						imgSrc={thumbnailUrl}
						title={title}
						subtitle={getAnimationSubtitle(id)}
						isOn={id ? enabledGraphicsCurrentShot.includes(id) : false}
						toggle={() => toggleGraphicState(id)}
						onThumbnailClick={() => editOverlay(id)}
						graphicId={id ?? 0}
						toggleHidden={roomState === ROOM_STATES.EDITING}
						isDisabled={!graphicIsEnabled(id)}
					/>
				))
			}
		</div>
	</>);
}
